import React from "react";
import Cta from "@qlik/qlik/src/components/Cta";
import Media from "@qlik/qlik/src/components/Media";
import mapButtonType from "@qlik/qlik/src/components/Cta/mapButtonType";
import {
    getCardDescriptionText,
    getCardEyebrowText,
    getCardMedia,
    renderSanitizedHtmlContent,
} from "@qlik/qlik/src/lib/util/helpers";
import RichText from "@qlik/qlik/src/components/RichText";
import * as styles from "./styles.module.css";

/**
 * renderCardContent component.
 * Renders a single card with an icon, category, title, headlineMBottom, subtitle, subtitleMBottom, paragraph, paragraphMBottom, and richTextBody.
 * @param {string} icon - The icon for the card.
 * @param {string} category - The category of the card.
 * @param {string} title - The title for the card.
 * @param {string} headlineMBottom - The margin bottom of the card title.
 * @param {string} subtitle - The subtitle for the card.
 * @param {string} subtitleMBottom - The margin bottom of the card sub title.
 * @param {string} paragraph - The paragraph for the card.
 * @param {string} paragraphMBottom - The margin bottom of the card paragraph.
 * @param {string} richTextBody - The description for the card.
 * @returns {JSX.Element} - The rendered component.
 */
const renderCardContent = (
    icon,
    category,
    title,
    headlineMBottom,
    subtitle,
    subtitleMBottom,
    paragraph,
    paragraphMBottom,
    richTextBody
) => (
    <div className="flex-1">
        {icon && <Media media={icon} className="w-auto object-contain mb-3.75 lg:mb-7.5 min-h-12 object-left" />}
        {category && <p className="tag-2 mb-3.75 uppercase">{category}</p>}
        {title && (
            <h3
                className={`card-title title-5 ${headlineMBottom}`}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: renderSanitizedHtmlContent(title) }}
            />
        )}
        {subtitle && <h4 className={`body text-blue ${subtitleMBottom}`}>{subtitle}</h4>}
        {paragraph && <p className={`leading-snug ${paragraphMBottom}`}>{paragraph}</p>}
        {richTextBody}
    </div>
);

/**
 * SingleIconCardWithShadow component.
 * Renders a single card with an icon, title, description, subtitle, and cta links.
 * @param {Object} props - The component props.
 * @param {string} props.icon - The icon for the card.
 * @param {string} props.title - The title of the card.
 * @param {Object} props.body - The description for the card.
 * @param {Object} props.subtitle - The sub title for the card.
 * @param {Object[]} props.cta - The cta links for the card.
 * @returns {JSX.Element} - The rendered component.
 */
const SingleIconCardWithShadow = ({ data }) => {
    const { title, body, cta, subtitle } = data ?? {};

    const cardMedia = getCardMedia(data);

    const paragraph = getCardDescriptionText(data);

    const ctaLinks = cta?.filter((link) => link !== "");

    const category = getCardEyebrowText(data);

    // Parse list items if they exist
    const richTextBody = <RichText data={data} className={`${ctaLinks ? "hasLinks" : "noLinks"}`} /> ?? null;

    // Determine margins for headline and paragraph
    const headlineMBottom = paragraph || subtitle ? "mb-3.5" : !paragraph && !subtitle ? "mb-0" : "mb-3.75";
    const subtitleMBottom = paragraph || richTextBody ? "mb-3.75" : "mb-7.5";
    const paragraphMBottom = richTextBody ? "mb-0" : "mb-7.5";

    // Determine class for list items
    const listClass = body && ctaLinks?.length === 0 ? "no-links" : "";

    const cardContent = renderCardContent(
        cardMedia,
        category,
        title,
        headlineMBottom,
        subtitle,
        subtitleMBottom,
        paragraph,
        paragraphMBottom,
        richTextBody
    );
    const commonCardStyles = "flex flex-col pt-5 pl-5 pr-4.75 pb-5 bg-white shadow-lg rounded h-full w-full";
    const cardStyles = `${commonCardStyles} ${listClass} ${styles.mobileTitle} ${styles.paragraphSpacing}`;

    // The card will be displayed as a wrap-all if the CTA contains a single link.
    if (ctaLinks && ctaLinks.length === 1) {
        const link = ctaLinks[0];
        return (
            <Cta data={link} className={`${cardStyles} ${styles.fullClickable}  after:hidden`}>
                {cardContent}
                {link && (
                    <div className="list-none">
                        <p className={`${mapButtonType(link?.type)} link-text`}>{link?.text}</p>
                    </div>
                )}
            </Cta>
        );
    }

    // The card will display as an inner link if the Call to Action (CTA) contains multiple links.
    return (
        <div className={`${cardStyles}`}>
            {cardContent}

            {ctaLinks && ctaLinks.length > 0 && (
                <div className="list-none">
                    {cta.map((link, index) => (
                        <Cta
                            key={link.id}
                            data={link}
                            className={`block ${index === ctaLinks.length - 1 ? "mb-0" : "mb-3.75"}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default SingleIconCardWithShadow;
